import { LIMITS, type ConversationMember, type ConversationSummary, type FileInfo, type HistoryMode } from '@sd/contracts';
import { useEffect, useState, type FormEvent } from 'react';
import { formatBytes, formatListTime } from '../format';
import { Icon } from '../icons/Icon';
import { Avatar } from '../ui/Avatar';
import { Button, IconButton, Notice, Switch, Tag } from '../ui/controls';
import { useConfirm, usePopover, useToast } from '../ui/overlays';
import { FileIcon, isPreviewTile, ScanTag, usePreviewUrl } from './attachments';
import { ConversationAvatar, usePerson } from './bits';
import { useAppEnv, useClient, useClientState } from './context';
import { errorMessage } from '../errors';
import { conversationName, isSelfChat } from './copy';
import { useShell } from './shell';

function FileRow({ file }: { file: FileInfo }) {
  const env = useAppEnv();
  const shell = useShell();
  return (
    <div className="fl">
      <FileIcon ext={file.ext} small />
      <div className="fl-main">
        <div className="fl-name ell">{file.name}</div>
        <div className="fl-meta">
          {formatBytes(file.size)} · {formatListTime(file.createdAt, env.now())} · <ScanTag file={file} />
        </div>
      </div>
      {file.messageId ? <IconButton icon="right" iconSize="sm" label="원래 메시지로 이동" onClick={() => shell.openConversation(file.conversationId, { messageId: file.messageId! })} /> : null}
    </div>
  );
}

function Thumb({ file, gallery }: { file: FileInfo; gallery: FileInfo[] }) {
  const env = useAppEnv();
  const url = usePreviewUrl(file, { size: 'tile' });
  return (
    <button type="button" className="shot v1" style={{ position: 'relative', aspectRatio: '1', borderRadius: 14, overflow: 'hidden' }} onClick={() => env.openModal({ kind: 'preview', file, gallery })} aria-label={`${file.name} 크게 보기`}>
      {url ? <img src={url} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} /> : null}
    </button>
  );
}

function PinButton({ conversation }: { conversation: ConversationSummary }) {
  const client = useClient();
  const toast = useToast();
  const pin = async () => {
    const pinned = !conversation.my.pinned;
    try {
      await client.updateMine(conversation.id, { pinned });
      toast(pinned ? '대화 목록 위쪽 고정됨에 두었어요' : '고정을 해제했어요');
    } catch (error) {
      toast(errorMessage(error));
    }
  };
  return (
    <button type="button" className={`dq${conversation.my.pinned ? ' on' : ''}`} aria-pressed={conversation.my.pinned} onClick={() => void pin()}>
      <span className="ic">
        <Icon name="pin" />
      </span>
      {conversation.my.pinned ? '고정됨' : '고정'}
    </button>
  );
}

/** The group's name, which its owner and admins can change in place. */
function GroupTitle({ conversation, canManage }: { conversation: ConversationSummary; canManage: boolean }) {
  const client = useClient();
  const toast = useToast();
  const name = conversationName(conversation);
  const [draft, setDraft] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (draft === null || !canManage) {
    return (
      <h3 className="d-title">
        <span>{name}</span>
        {canManage ? <IconButton icon="compose" iconSize="sm" label="그룹 이름 바꾸기" onClick={() => setDraft(conversation.title ?? name)} /> : null}
      </h3>
    );
  }
  const title = draft.trim();
  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (!title || busy) return;
    if (title === conversation.title) return setDraft(null);
    setBusy(true);
    try {
      await client.renameGroup(conversation.id, title);
      toast('그룹 이름을 바꿨어요');
      setDraft(null);
    } catch (error) {
      toast(errorMessage(error));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="d-rename" onSubmit={(event) => void save(event)}>
      <div className="field">
        <input
          value={draft}
          maxLength={LIMITS.maxGroupTitleChars}
          aria-label="그룹 이름"
          autoFocus
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.stopPropagation();
              setDraft(null);
            }
          }}
        />
      </div>
      <div className="d-rename-actions">
        <Button size="sm" variant="ghost" onClick={() => setDraft(null)}>
          취소
        </Button>
        <Button size="sm" variant="primary" type="submit" disabled={!title || busy}>
          저장
        </Button>
      </div>
    </form>
  );
}

/**
 * What the viewer may do to a member, by the server's rules: the group owner makes and unmakes admins;
 * the owner and admins remove members, an admin not another admin; nobody acts on the owner or themselves.
 */
export function memberActions(myRole: ConversationMember['role'], target: ConversationMember, meId: string): { role: 'admin' | 'member' | null; remove: boolean } {
  if (target.person.id === meId || target.role === 'owner') return { role: null, remove: false };
  return {
    role: myRole === 'owner' ? (target.role === 'admin' ? 'member' : 'admin') : null,
    remove: myRole === 'owner' || (myRole === 'admin' && target.role === 'member'),
  };
}

function MemberMenu({ conversation, member, meId }: { conversation: ConversationSummary; member: ConversationMember; meId: string }) {
  const client = useClient();
  const confirm = useConfirm();
  const toast = useToast();
  const popover = usePopover();
  const actions = memberActions(conversation.my.role, member, meId);
  if (!actions.role && !actions.remove) return null;
  const name = member.person.displayName;
  const setRole = async (role: 'admin' | 'member') => {
    try {
      await client.setMemberRole(conversation.id, member.person.id, role);
      toast(role === 'admin' ? `${name} 님을 그룹 관리자로 지정했어요` : `${name} 님의 그룹 관리자 역할을 해제했어요`);
    } catch (error) {
      toast(errorMessage(error));
    }
  };
  const remove = async () => {
    const ok = await confirm({
      title: `${name} 님을 그룹에서 내보낼까요?`,
      body: '내보낸 사람은 이 그룹의 대화와 파일을 더 볼 수 없어요. 다시 참여하려면 초대가 필요해요.',
      ok: '내보내기',
      danger: true,
    });
    if (!ok) return;
    try {
      await client.removeMember(conversation.id, member.person.id);
      toast(`${name} 님을 그룹에서 내보냈어요`);
    } catch (error) {
      toast(errorMessage(error));
    }
  };
  const open = (anchor: HTMLElement) =>
    popover.open(
      anchor,
      (close) => (
        <>
          {actions.role ? (
            <button
              type="button"
              role="menuitem"
              className="pi"
              onClick={() => {
                close();
                void setRole(actions.role!);
              }}
            >
              <Icon name="shield" size="sm" />
              {actions.role === 'admin' ? '그룹 관리자로 지정' : '그룹 관리자 해제'}
            </button>
          ) : null}
          {actions.remove ? (
            <button
              type="button"
              role="menuitem"
              className="pi danger"
              onClick={() => {
                close();
                void remove();
              }}
            >
              <Icon name="userminus" size="sm" />
              그룹에서 내보내기
            </button>
          ) : null}
        </>
      ),
      { align: 'right', role: 'menu', label: `${name} 멤버 메뉴` },
    );
  return <IconButton icon="more" iconSize="sm" label={`${name} 멤버 메뉴`} aria-haspopup="menu" onClick={(event) => open(event.currentTarget)} />;
}

/**
 * Before the owner leaves: the role passes to the longest-standing admin, so with others still in the group and no
 * admin among them the server refuses. Returns that admin, `blocked` or null (alone or not the owner).
 */
export function ownerHandover(myRole: ConversationMember['role'], members: readonly ConversationMember[], meId: string): ConversationMember | 'blocked' | null {
  if (myRole !== 'owner') return null;
  const others = members.filter((m) => m.person.id !== meId);
  if (!others.length) return null;
  const admins = others.filter((m) => m.role === 'admin').sort((a, b) => Date.parse(a.joinedAt) - Date.parse(b.joinedAt));
  return admins[0] ?? 'blocked';
}

/** Conversation info panel (design `aside.detail`). */
export function DetailPanel() {
  const env = useAppEnv();
  const shell = useShell();
  const client = useClient();
  const confirm = useConfirm();
  const toast = useToast();
  const conversation = useClientState((s) => (s.activeConversationId ? s.conversations[s.activeConversationId] : undefined));
  const details = useClientState((s) => (s.activeConversationId ? s.details[s.activeConversationId] : undefined));
  const files = useClientState((s) => (s.activeConversationId ? s.conversationFiles[s.activeConversationId] : undefined));
  const meId = useClientState((s) => s.me?.id ?? '');
  const peer = usePerson(conversation?.peer?.id);
  const visible = shell.detailOpen || (shell.single && shell.view === 'detail');
  const conversationId = conversation?.id;

  useEffect(() => {
    if (visible && conversationId) void client.loadConversationFiles(conversationId).catch(() => {});
  }, [client, conversationId, visible]);

  const head = (
    <div className="d-head">
      <IconButton icon="back" className="ch-back" label="대화로 돌아가기" style={{ display: shell.single ? 'inline-grid' : 'none' }} onClick={() => shell.setView('chat')} />
      <h2>대화 정보</h2>
      <IconButton icon="x" label="정보 패널 닫기" onClick={() => (shell.single ? shell.setView('chat') : shell.toggleDetail(false))} />
    </div>
  );
  if (!conversation) return <aside className="detail pane glass scroll" aria-label="대화 정보">{head}</aside>;

  const mute = async () => {
    const muted = !conversation.my.muted;
    try {
      await client.updateMine(conversation.id, { muted });
      toast(muted ? '이 대화를 음소거했어요' : '음소거를 해제했어요');
    } catch (error) {
      toast(errorMessage(error));
    }
  };

  // Images too large to preview are listed with the files rather than as empty thumbnails.
  const fileList = (files ?? []).filter((f) => !isPreviewTile(f) || conversation.kind === 'direct');
  // The newest three show; the viewer goes through all of them, oldest first (the list comes newest first).
  const pictures = (files ?? []).filter(isPreviewTile);
  const images = pictures.slice(0, 3);
  const gallery = [...pictures].reverse();
  const fileCard = fileList.length ? (
    <div className="flist">
      {fileList.slice(0, 8).map((f) => (
        <FileRow key={f.id} file={f} />
      ))}
    </div>
  ) : (
    <p className="desc">아직 공유된 파일이 없어요.</p>
  );

  if (conversation.kind === 'group') {
    const left = conversation.my.state !== 'active';
    const canManage = conversation.my.role === 'owner' || conversation.my.role === 'admin';
    const members = details?.members ?? [];
    const history = conversation.historyMode ?? 'after_join';
    const today = env.now().toDateString();
    const onHistory = async (value: HistoryMode) => {
      if (value === history || !canManage) return;
      const ok = await confirm(
        value === 'include_history'
          ? { title: '기존 내역을 공유할까요?', body: '새로 참여하는 사람을 포함한 참여자가 보존된 이전 대화와 파일을 볼 수 있게 돼요. 변경 사실은 참여자 모두에게 알림으로 가요.', ok: '기존 내역 공유' }
          : { title: '참여 이후 메시지만 보이게 할까요?', body: '앞으로 참여하는 사람은 참여한 뒤의 메시지만 볼 수 있어요. 변경 사실은 참여자에게 알림으로 가요.', ok: '변경' },
      );
      if (!ok) return;
      try {
        await client.setHistoryMode(conversation.id, value);
        toast('과거 내역 공유 설정을 바꾸고 참여자에게 알렸어요');
      } catch (error) {
        toast(errorMessage(error));
      }
    };
    const leave = async () => {
      const heir = details ? ownerHandover(conversation.my.role, members, meId) : null;
      if (heir === 'blocked') {
        await confirm({
          title: '나가기 전에 그룹 관리자를 정해 주세요',
          body: '그룹 소유자가 나가면 소유권이 그룹 관리자에게 넘어가요. 멤버 목록에서 한 사람의 메뉴(…)를 열어 그룹 관리자로 지정한 뒤 다시 나가 주세요.',
          ok: '확인',
        });
        return;
      }
      const ok = await confirm({
        title: '그룹에서 나갈까요?',
        body: `나간 시점까지의 대화는 읽기 전용으로 남고, 새 메시지는 받을 수 없어요. 다시 참여하려면 초대가 필요해요.${heir ? ` 그룹 소유권은 ${heir.person.displayName} 님에게 넘어가요.` : ''}`,
        ok: '나가기',
        danger: true,
      });
      if (!ok) return;
      try {
        await client.leave(conversation.id);
        toast('그룹에서 나갔어요');
      } catch (error) {
        // The members changed meanwhile and no admin is left to take over (the server answers CONFLICT).
        toast(errorMessage(error, conversation.my.role === 'owner' ? { CONFLICT: '다른 멤버를 그룹 관리자로 지정한 뒤 나갈 수 있어요' } : {}));
      }
    };
    return (
      <aside className="detail pane glass scroll" id="detail" aria-label="대화 정보">
        {head}
        <div className="d-hero">
          <ConversationAvatar conversation={conversation} size={84} />
          <GroupTitle key={conversation.id} conversation={conversation} canManage={canManage && !left} />
          <p>
            그룹 · {conversation.memberCount}명{left ? ' · 나간 대화' : ''}
          </p>
        </div>
        {left ? (
          <div className="d-card">
            <Notice tone="neutral" icon="lock">
              나간 대화예요. 나간 시점까지의 대화만 읽기 전용으로 볼 수 있고, 새 메시지는 받을 수 없어요.
            </Notice>
          </div>
        ) : (
          <div className="d-quick">
            <button type="button" className={`dq${conversation.my.muted ? ' on' : ''}`} onClick={() => void mute()}>
              <span className="ic">
                <Icon name={conversation.my.muted ? 'belloff' : 'bell'} />
              </span>
              {conversation.my.muted ? '음소거됨' : '알림'}
            </button>
            <PinButton conversation={conversation} />
            <button type="button" className="dq" onClick={() => env.openModal({ kind: 'search', scope: conversation.id })}>
              <span className="ic">
                <Icon name="search" />
              </span>
              검색
            </button>
            {canManage ? (
              <button type="button" className="dq" onClick={() => env.openModal({ kind: 'people', mode: 'add-member' })}>
                <span className="ic">
                  <Icon name="userplus" />
                </span>
                멤버 초대
              </button>
            ) : null}
          </div>
        )}
        <div className="d-card">
          <h4>
            <Icon name="users" size="sm" />
            멤버 {conversation.memberCount}
          </h4>
          {members.map((m) => (
            <div key={m.person.id} className="mem">
              <Avatar person={m.person} />
              <div className="mem-main">
                <div className="mem-name">
                  {m.person.displayName}
                  {m.role === 'owner' ? <Tag tone="accent">소유자</Tag> : m.role === 'admin' ? <Tag tone="blue">관리자</Tag> : null}
                  {m.person.id === meId ? <Tag>나</Tag> : null}
                  {m.person.id !== meId && new Date(m.joinedAt).toDateString() === today && new Date(m.joinedAt).getTime() > new Date(conversation.createdAt).getTime() + 60_000 ? <Tag tone="ok">오늘 참여</Tag> : null}
                </div>
                <div className="mem-sub ell">{m.person.department}</div>
              </div>
              {left ? null : <MemberMenu conversation={conversation} member={m} meId={meId} />}
            </div>
          ))}
          {conversation.memberCount > members.length && members.length ? <p className="desc">외 {conversation.memberCount - members.length}명</p> : null}
          {left ? null : canManage ? (
            <Button size="sm" variant="ghost" block icon="userplus" style={{ marginTop: 8 }} onClick={() => env.openModal({ kind: 'people', mode: 'add-member' })}>
              멤버 초대
            </Button>
          ) : (
            <p className="desc">멤버 초대와 내보내기는 그룹 소유자와 관리자가 해요.</p>
          )}
        </div>
        {left ? null : (
          <>
            <div className="d-card">
              <h4>
                <Icon name="history" size="sm" />
                과거 내역 공유 {canManage ? <Tag tone="blue">그룹 관리자</Tag> : <Tag>관리자만 변경</Tag>}
              </h4>
              {(['after_join', 'include_history'] as const).map((value) => (
                <label key={value} className={`radio${history === value ? ' sel' : ''}`}>
                  <input type="radio" name="hist" value={value} checked={history === value} disabled={!canManage} onChange={() => void onHistory(value)} />
                  <span>
                    {value === 'after_join' ? (
                      <>
                        <b>
                          참여 이후 메시지만 <Tag>기본</Tag>
                        </b>
                        <span>새 참여자는 참여한 뒤의 메시지만 볼 수 있어요.</span>
                      </>
                    ) : (
                      <>
                        <b>기존 내역 포함</b>
                        <span>보존된 이전 대화·파일도 볼 수 있어요.</span>
                      </>
                    )}
                  </span>
                </label>
              ))}
              <p className="desc">변경하면 참여자에게 알림이 가요. 검색·답장 인용·파일 다운로드에도 같은 범위가 적용돼요.</p>
            </div>
            <div className="d-card">
              <div className="set-row">
                <div className="sr-main">
                  <b>이 대화 음소거</b>
                  <span>배지는 회색으로 표시되고 알림이 울리지 않아요</span>
                </div>
                <Switch checked={conversation.my.muted} onChange={() => void mute()} label="이 대화 음소거" />
              </div>
            </div>
          </>
        )}
        <div className="d-card">
          <h4>
            <Icon name="doc" size="sm" />
            파일
          </h4>
          {fileCard}
        </div>
        {images.length ? (
          <div className="d-card">
            <h4>
              <Icon name="image" size="sm" />
              이미지
            </h4>
            <div className="igrid">
              {images.map((f) => (
                <Thumb key={f.id} file={f} gallery={gallery} />
              ))}
            </div>
          </div>
        ) : null}
        {left ? null : (
          <>
            <button type="button" className="leave-btn" onClick={() => void leave()}>
              <Icon name="logout" size="sm" />
              그룹 나가기
            </button>
            <p className="leave-note">나간 시점까지의 대화는 읽기 전용으로 남아요.</p>
          </>
        )}
      </aside>
    );
  }

  const p = peer ?? conversation.peer;
  if (isSelfChat(conversation)) {
    return (
      <aside className="detail pane glass scroll" id="detail" aria-label="대화 정보">
        {head}
        <div className="d-hero">
          <Avatar person={p} size={84} noDot />
          <h3>나와의 채팅</h3>
          <p>나만 보는 대화예요. 메모나 파일을 보내 두면 내 다른 기기에서도 볼 수 있어요.</p>
        </div>
        <div className="d-quick">
          <PinButton conversation={conversation} />
          <button type="button" className="dq" onClick={() => env.openModal({ kind: 'search', scope: conversation.id })}>
            <span className="ic">
              <Icon name="search" />
            </span>
            검색
          </button>
        </div>
        <div className="d-card">
          <h4>
            <Icon name="doc" size="sm" />
            보관한 파일
          </h4>
          {fileList.length ? fileCard : <p className="desc">아직 보관한 파일이 없어요.</p>}
        </div>
      </aside>
    );
  }
  const deactivated = conversation.readOnlyReason === 'peer_deactivated';
  return (
    <aside className="detail pane glass scroll" id="detail" aria-label="대화 정보">
      {head}
      <div className="d-hero">
        <Avatar person={p} size={84} />
        <h3>{p?.displayName}</h3>
        <p>
          {p?.department}
          {deactivated ? '' : ` · ${p?.online ? '온라인' : '오프라인'}`}
        </p>
        {deactivated ? <Tag>비활성화된 계정</Tag> : null}
      </div>
      {deactivated ? (
        <div className="d-card">
          <Notice tone="neutral" icon="lock">
            이 계정은 비활성화됐어요. 기존 대화와 파일은 회사 보존 정책에 따라 읽기 전용으로 남아요.
          </Notice>
        </div>
      ) : (
        <>
          <div className="d-quick">
            <button type="button" className={`dq${conversation.my.muted ? ' on' : ''}`} onClick={() => void mute()}>
              <span className="ic">
                <Icon name={conversation.my.muted ? 'belloff' : 'bell'} />
              </span>
              {conversation.my.muted ? '음소거됨' : '알림'}
            </button>
            <PinButton conversation={conversation} />
            <button type="button" className="dq" onClick={() => env.openModal({ kind: 'search', scope: conversation.id })}>
              <span className="ic">
                <Icon name="search" />
              </span>
              검색
            </button>
            <button type="button" className="dq" onClick={() => env.openModal({ kind: 'people', mode: 'add-peer' })}>
              <span className="ic">
                <Icon name="userplus" />
              </span>
              상대 추가
            </button>
          </div>
          <div className="d-card">
            <h4>
              <Icon name="mail" size="sm" />
              프로필
            </h4>
            <div className="set-row">
              <div className="sr-main">
                <span>이메일</span>
                <b className="ell">{p?.email}</b>
              </div>
              <IconButton
                icon="copy"
                iconSize="sm"
                label="이메일 복사"
                onClick={() => {
                  void navigator.clipboard?.writeText(p?.email ?? '').catch(() => {});
                  toast('복사했어요');
                }}
              />
            </div>
            <div className="set-row">
              <div className="sr-main">
                <span>부서</span>
                <b>{p?.department}</b>
              </div>
            </div>
          </div>
        </>
      )}
      <div className="d-card">
        <h4>
          <Icon name="doc" size="sm" />
          공유된 파일
        </h4>
        {fileCard}
      </div>
      {deactivated ? null : (
        <div className="d-card">
          <h4>
            <Icon name="userplus" size="sm" />
            대화 상대 추가
          </h4>
          <p className="desc" style={{ margin: '0 0 10px' }}>
            새 그룹 대화가 만들어져요. 기존 1:1 내역은 공유되지 않아요.
          </p>
          <Button size="sm" variant="ghost" block icon="userplus" onClick={() => env.openModal({ kind: 'people', mode: 'add-peer' })}>
            사람 추가해서 그룹 만들기
          </Button>
        </div>
      )}
    </aside>
  );
}
